import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';

import {
  Flex,
  Modal,
  P,
  TagInput,
  TextArea,
  TextField,
} from '@decisiv/ui-components';
import Pencil from '@decisiv/iconix/lib/components/Pencil';
import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';

import normalizeTags from '../../utils/normalizeTags';

export default function WmiCodeFormModal({
  visible,
  onClose,
  onSave,
  wmiCode = null,
}) {
  const isEditing = !!wmiCode;

  const [oemName, setOemName] = useState('');
  const [wmiCodes, setWmiCodes] = useState([]);
  const [tags, setTags] = useState([]);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (wmiCode) {
      setOemName(wmiCode.attributes.oem_name || '');
      setWmiCodes(wmiCode.attributes.wmi_codes || []);
      setTags(normalizeTags(wmiCode.attributes.tags));
      setDescription(wmiCode.attributes.description || '');
    } else {
      setOemName('');
      setWmiCodes([]);
      setTags([]);
      setDescription('');
    }
    setErrors({});
  }, [wmiCode, visible]);

  const validate = () => {
    const newErrors = {};
    if (!oemName.trim()) {
      newErrors.oemName = t`OEM Name is required`;
    } else if (oemName.trim().startsWith('tag:')) {
      newErrors.oemName = t`OEM Name cannot start with the reserved prefix "tag:"`;
    }
    if (wmiCodes.length === 0) {
      newErrors.wmiCodes = t`At least one WMI code is required`;
    } else if (wmiCodes.some((code) => code.length !== 3)) {
      newErrors.wmiCodes = t`Each WMI code must be exactly 3 characters`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const attributes = {
      oem_name: oemName.trim(),
      wmi_codes: wmiCodes,
      tags: normalizeTags(tags),
      description: description.trim() || null,
    };

    onSave(attributes, wmiCode?.id);
  };

  return (
    <Modal
      actions={[
        {
          text: t`Cancel`,
          onClick: onClose,
          kind: 'secondary',
        },
        {
          text: isEditing ? t`Update` : t`Create`,
          onClick: handleSave,
          kind: 'primary',
        },
      ]}
      intent="information"
      icon={isEditing ? Pencil : PlusCircle}
      title={isEditing ? t`Edit WMI Code` : t`Create WMI Code`}
      visible={visible}
      onClose={onClose}
    >
      <Flex
        flexDirection="column"
        gap={0}
        style={{ minWidth: '600px', minHeight: '250px' }}
      >
        <TextField
          label={t`OEM Name`}
          value={oemName}
          onChange={(e) => setOemName(e.target.value)}
          required
        />
        {errors.oemName && <P intent="danger">{errors.oemName}</P>}
        <Flex marginTop={1}>
          <div
            role="presentation"
            style={{ display: 'contents' }}
            onKeyDown={(e) => {
              // TagInput only commits on Enter. Translate space into a synthetic
              // Enter keyup so the user can also separate codes with spaces.
              if (
                e.key === ' ' &&
                e.target?.tagName === 'INPUT' &&
                e.target.value?.trim()
              ) {
                e.preventDefault();
                e.target.dispatchEvent(
                  new KeyboardEvent('keyup', { key: 'Enter', bubbles: true }),
                );
              }
            }}
          >
            <TagInput
              label={t`WMI Codes`}
              helpMessage={t`Press Enter or use commas/spaces to add multiple WMI codes at once`}
              tags={wmiCodes}
              onChange={(newCodes) => {
                const expanded = newCodes.flatMap((code) =>
                  code.split(/[,\s]+/).filter(Boolean),
                );
                const upper = expanded.map((code) => code.toUpperCase());
                setWmiCodes([...new Set(upper)]);
              }}
            />
          </div>
        </Flex>
        {errors.wmiCodes && <P intent="danger">{errors.wmiCodes}</P>}
        <Flex marginTop={1} flexDirection="column">
          <div
            role="presentation"
            style={{ display: 'contents' }}
            onKeyDown={(e) => {
              // TagInput only commits on Enter. Translate space into a synthetic
              // Enter keyup so the user can also separate tags with spaces.
              if (
                e.key === ' ' &&
                e.target?.tagName === 'INPUT' &&
                e.target.value?.trim()
              ) {
                e.preventDefault();
                e.target.dispatchEvent(
                  new KeyboardEvent('keyup', { key: 'Enter', bubbles: true }),
                );
              }
            }}
          >
            <TagInput
              label={t`Tags`}
              helpMessage={t`Press Enter or use commas/spaces to add multiple tags at once`}
              tags={tags}
              onChange={(newTags) => {
                const expanded = newTags.flatMap((tag) =>
                  tag.split(/[,\s]+/).filter(Boolean),
                );
                setTags([...new Set(expanded)]);
              }}
            />
          </div>
        </Flex>
        <Flex marginTop={1}>
          <TextArea
            label={t`Description`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Flex>
      </Flex>
    </Modal>
  );
}

WmiCodeFormModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  wmiCode: PropTypes.shape({
    id: PropTypes.string,
    attributes: PropTypes.shape({
      oem_name: PropTypes.string,
      wmi_codes: PropTypes.arrayOf(PropTypes.string),
      tags: PropTypes.arrayOf(PropTypes.string),
      description: PropTypes.string,
    }),
  }),
};
